import React from "react";

import withStyles from '@mui/styles/withStyles';
import classnames from "classnames";
import { useTranslation } from "react-i18next";

import { ShipmentTracking } from "../../../../../../models/shipment.tracking";
import { calculateDelayedDays } from "../../../../../../util/calculateDelayedDays";
import { capitalizeFirstLetterAllWorlds } from "../../../../../../util/capitalizeFirstLetter";

interface Props {
  shipmentTracking?: ShipmentTracking;
}
type BadgeProps = {
  value?: string;
  backgroundColor?: string;
  dataCy?: string;
  textColor?: string;
  classes?: any;
  className?: string;
};

const styles = () =>
  ({
    customBadge: {
      display: "flex",
      alignItems: "center",
      backgroundColor: (props: { textColor?: string; backgroundColor?: string }) =>
        props?.backgroundColor,
      padding: "8px 12px",
      height: "32px",
      borderRadius: "100px",
      whiteSpace: "nowrap",
      width: "fit-content",
    },
    textValue: {
      color: (props: { textColor?: string }) => props?.textColor,
      fontFamily: "Manrope",
      fontSize: "12px",
      fontWeight: 600,
      lineHeight: "16px",
    },
  } as const);

const SimpleBadge = (props: BadgeProps) => {
  const { classes, value, className, dataCy } = props;

  return (
    <div className={classnames(classes.customBadge, className)}>
      <span className={classes.textValue} data-cy={dataCy}>
        {value}
      </span>
    </div>
  );
};

const StyledBadge = withStyles(styles)(SimpleBadge);

const renderBadge = (
  backgroundColor: string,
  textColor: string,
  value?: string,
  formatWord = true
) => {
  if (value) {
    const formatted = formatWord ? capitalizeFirstLetterAllWorlds(value) : value;

    return (
      <StyledBadge backgroundColor={backgroundColor} textColor={textColor} value={formatted} />
    );
  }

  return <> </>;
};

const DelayedBadge = (props: Props) => {
  const { t: translate } = useTranslation();
  const { shipmentTracking } = props;

  if (!shipmentTracking) return null;

  const { finalPortPredictiveArrival, finalPortPredictiveArrivalLast } = shipmentTracking;

  const delayedDays = calculateDelayedDays(
    finalPortPredictiveArrival,
    finalPortPredictiveArrivalLast
  );

  const getBadgeByDelay = (days?: number) => {
    if (days === undefined || days === null) return null;

    if (days >= 0 && days <= 3)
      return renderBadge(
        "#F2F4F7",
        "#475467",
        `${days} ${days >= 1 ? translate("days").toLowerCase() : translate("day").toLowerCase()}`
      );
    if (days > 3 && days <= 5)
      return renderBadge("#FFFAEB", "#DC6803", `${days} ${translate("days").toLowerCase()}`);

    return renderBadge("#FEF3F2", "#D92D20", `${days} ${translate("days").toLowerCase()}`);
  };

  return delayedDays !== undefined ? getBadgeByDelay(delayedDays) : null;
};

export default DelayedBadge;
